/**
 * File: ContractAnalysis.jsx
 * Path: /src/pages/ContractAnalysis.jsx
 * Description: Dedicated page showcasing Veritect.ai's Contract Analysis AI solution
 * Key Features: Interactive contract visualization, feature breakdown, and benefits section
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ContractAnalysisDashboard from '../components/solutions/ContractAnalysisDashboard';
import ContractAnalysisComparisonDashboard from '../components/solutions/ContractAnalysisComparisonDashboard';
import ModernCtaSection from '../components/common/ModernCtaSection';
import ModernStatsSection from '../components/common/ModernStatsSection';
import useIntersectionObserver from '../hooks/useIntersectionObserver';

const ContractAnalysis = () => {
  // Initialize observer for scroll animations
  const [sectionRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Features data
  const features = [
    {
      title: "Risk Identification",
      description: "Automatically identify and highlight clauses that present potential legal, financial, or operational risks.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )
    },
    {
      title: "Clause Extraction",
      description: "Extract, categorize, and compare key clauses across multiple contracts to identify discrepancies and inconsistencies.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      title: "Custom Clause Library",
      description: "Build and maintain a library of approved clauses and playbooks that align with your firm's best practices and policies.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
        </svg>
      )
    },
    {
      title: "Multi-Language Support",
      description: "Analyze contracts in multiple languages with the same level of accuracy and insight as English documents.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      )
    },
    {
      title: "Redlining & Revision Tracking",
      description: "Track changes and revisions throughout the contract lifecycle with clear versioning and approval workflows.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      )
    },
    {
      title: "Obligation Management",
      description: "Extract and monitor contractual obligations, deadlines, and renewal dates to ensure compliance and avoid missed opportunities.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  // How it works steps
  const workflowSteps = [
    {
      number: "01",
      title: "Upload Your Contracts",
      description: "Upload contracts in any format (PDF, Word, scanned documents) for instant analysis."
    },
    {
      number: "02",
      title: "AI-Powered Analysis",
      description: "Our system automatically identifies key clauses, potential risks, and obligations."
    },
    {
      number: "03",
      title: "Review Insights",
      description: "Get a clear breakdown of critical elements with risk scoring and recommendations."
    },
    {
      number: "04",
      title: "Collaborate & Revise",
      description: "Share findings with your team, track changes, and implement revisions."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-secondary-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-primary-100 rounded-tr-full opacity-70"></div>
        
        {/* Floating decorative elements */}
        <motion.div 
          className="absolute right-1/4 top-1/5 hidden md:block"
          animate={{ 
            y: [0, -15, 0], 
            opacity: [0.4, 0.7, 0.4] 
          }}
          transition={{ 
            duration: 5, 
            repeat: Infinity, 
            repeatType: "reverse" 
          }}
        >
          <div className="w-16 h-16 border-2 border-secondary-500/30 rounded-lg rotate-12"></div>
        </motion.div>
        
        <motion.div 
          className="absolute left-1/4 bottom-1/4 hidden md:block"
          animate={{ 
            y: [0, 15, 0], 
            opacity: [0.3, 0.6, 0.3] 
          }}
          transition={{ 
            duration: 7, 
            repeat: Infinity, 
            repeatType: "reverse", 
            delay: 1 
          }}
        >
          <div className="w-12 h-12 border-2 border-accent-500/30 rounded-full"></div>
        </motion.div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  AI-Powered <span className="gradient-text">Contract Analysis</span> Platform
                </h1>
                <p className="text-xl text-dark-600 mb-8">
                  Scan and analyze contracts of any length and complexity to identify key terms, risks, obligations, and opportunities with unparalleled accuracy and speed.
                </p>
                
                <div className="flex flex-wrap gap-4 mb-10">
                  <Button
                    variant="primary"
                    size="lg"
                    to="/contact"
                    className="shadow-lg relative overflow-hidden group"
                  >
                    <span className="relative z-10">Request a Demo</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    to="/solutions/contract-analysis#how-it-works"
                  >
                    See How It Works
                  </Button>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex -space-x-2">
                    {['#9333EA', '#8B5CF6', '#6366F1', '#4F46E5'].map((color, i) => (
                      <div key={i} className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: color, zIndex: 4-i }}>
                        {['B', 'W', 'D', 'P'][i]}
                      </div>
                    ))}
                  </div>
                  <div className="text-dark-500">
                    <strong className="text-dark-700">10M+</strong> contracts analyzed
                  </div>
                </div>
              </motion.div>
              
              {/* Right Side - Interactive Visualization */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
              >
                {/* Contract Analysis Dashboard Component */}
                <ContractAnalysisDashboard />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <ModernStatsSection 
        title="Transform Your Contract Review Process"
        description="See the real impact our AI-powered contract analysis platform is making for law firms."
        stats={[
          { number: '70%', label: 'Faster Review Time' },
          { number: '85%', label: 'Risk Detection Rate' },
          { number: '3.2x', label: 'More Contracts Reviewed' },
          { number: '67%', label: 'Cost Reduction' }
        ]} 
      />
      
      {/* Features Section */}
      <section className="py-20 bg-white" ref={sectionRef}>
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Key Features</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Powerful Contract Analysis Capabilities
            </h2>
            <p className="text-dark-500 text-lg">
              Our contract analysis platform combines cutting-edge AI with deep legal expertise 
              to deliver contract review that's faster, more comprehensive, and more insightful.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full flex flex-col items-start">
                  <div className="rounded-lg p-3 bg-secondary-50 text-secondary-600 mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-dark-500">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-secondary-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-64 h-64 bg-primary-600/5 rounded-full blur-3xl"></div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">How It Works</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Contract Analysis, Reimagined
            </h2>
            <p className="text-dark-500 text-lg">
              Our intuitive workflow makes contract review faster and more effective than ever before.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
              >
                {/* Connector line between steps (hidden on mobile) */}
                {index < workflowSteps.length - 1 && (
                  <div className="absolute hidden lg:block top-6 left-full w-full h-0.5 bg-secondary-200 z-0">
                    <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45">
                      <svg className="w-3 h-3 text-secondary-400" fill="currentColor" viewBox="0 0 256 512">
                        <path d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6.1 34z"></path>
                      </svg>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col items-center md:items-start text-center md:text-left">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-600 text-white text-xl font-bold mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-dark-500">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.7 }}
            >
              <span className="text-primary-600 font-semibold">Benefits</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Why Choose Veritect Contract Analysis AI
              </h2>
              <p className="text-dark-600 mb-8">
                Our AI-powered contract analysis platform doesn't just save time—it transforms how legal professionals
                review contracts, enabling deeper insights and better risk management.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    title: "Comprehensive Risk Assessment",
                    description: "Identify potential risks, obligations, and non-standard clauses that might otherwise be overlooked in manual review."
                  },
                  {
                    title: "Consistency Across Contracts",
                    description: "Ensure consistent language and terms across all your contracts and align with your organization's policies and playbooks."
                  },
                  {
                    title: "Time and Cost Efficiency",
                    description: "Reduce contract review time by up to 70%, allowing attorneys to focus on strategic decision-making rather than tedious document review."
                  },
                  {
                    title: "Scalable Review Process",
                    description: "Easily handle high volumes of contracts during due diligence, M&A transactions, or regulatory changes without expanding headcount."
                  }
                ].map((benefit, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <div className="mr-4 text-secondary-600 mt-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">{benefit.title}</h4>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-10">
                <Button 
                  variant="primary"
                  to="/contact"
                  className="shadow-lg"
                >
                  Schedule a Demo
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              {/* Contract Analysis Comparison Dashboard Component */}
              <ContractAnalysisComparisonDashboard />
              
              {/* Floating elements for visual interest */}
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-secondary-100 rounded-lg rotate-12 flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div className="absolute -bottom-4 -left-4 w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center z-20">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Testimonial Section */}
      <section className="py-20 bg-primary-50 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400"></div>
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="1">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" />
            </g>
          </svg>
        </div>
        
        <div className="container-padding mx-auto relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Hear From Our Clients
            </h2>
            <p className="text-dark-500 text-lg">
              Leading law firms are transforming their contract review process with Veritect.ai
            </p>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-xl shadow-lg p-8 relative max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Quote mark */}
            <div className="absolute top-6 left-8 text-secondary-300 opacity-30 text-6xl font-serif">
              "
            </div>
            
            <div className="pl-8">
              <blockquote className="text-lg mb-6 text-dark-700">
                The contract analysis tool has been a game-changer for our corporate practice. We've reduced 
                review time by 70% while increasing accuracy and risk identification. During a recent M&A 
                transaction, we were able to analyze over 500 contracts in just three days—a task that would 
                have taken weeks with traditional methods.
              </blockquote>
              
              <div className="flex items-center">
                <div className="mr-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-secondary-500 to-primary-500 flex items-center justify-center text-white text-lg font-bold">
                    MC
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-dark-800">Michael Chen</h4>
                  <p className="text-dark-500 text-sm">Head of Legal Operations, Pacific Law Group</p>
                </div>
                <div className="ml-auto flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent-500" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* FAQs Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-primary-600 font-semibold">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-dark-500 text-lg">
              Everything you need to know about our Contract Analysis AI platform
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "What types of contracts can Veritect's platform analyze?",
                answer: "Our platform can analyze virtually any type of contract, including NDAs, service agreements, employment contracts, leases, vendor agreements, software licenses, and more. The system is trained on millions of contracts and continuously improves with each new document it processes."
              },
              {
                question: "How accurate is the risk identification?",
                answer: "Our platform achieves an 85% accuracy rate in identifying contractual risks, which exceeds the average accuracy of junior attorneys in benchmark tests. All AI outputs are designed to be verified by legal professionals, with clear highlighting and explanations for identified issues."
              },
              {
                question: "How does the platform handle different languages?",
                answer: "The system currently supports contract analysis in 12 languages, including English, Spanish, French, German, Italian, Portuguese, Chinese, Japanese, Korean, Dutch, Russian, and Arabic. Additional languages are being added regularly based on client needs."
              },
              {
                question: "Can I customize the analysis to my firm's specific requirements?",
                answer: "Yes, the platform is fully customizable to your firm's specific requirements and risk tolerance. You can create custom playbooks, clause libraries, and risk scoring criteria. The system also learns from your feedback and adjustments to improve future analyses."
              }
            ].map((faq, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              >
                <Card className="transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                  <p className="text-dark-600">{faq.answer}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <ModernCtaSection
        title="Ready to Transform Your Contract Review Process?"
        description="Join hundreds of forward-thinking law firms using Veritect.ai to streamline contract analysis and mitigate risks."
        buttonText="Schedule a Demo"
        buttonLink="/contact"
      />
    </motion.div>
  );
};

export default ContractAnalysis;